import { type FC } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { LoadingSpinner, Tag } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { renderAaFeatureAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type IAaFeatureState, loadAaFeature } from "@/redux/modules/aaFeature.ts";
import { type ISortableTrack, getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { TRACK_TYPE } from "@/settings/settings.js";

export const AA_FEATURE_MISSING_GENOME_MESSAGE =
  "Genome view is not available because this feature does not belong to a specific genome.";

const AaFeatureGenomeView: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { aaFeature, isLoading } = useSelector<IAppState, IAaFeatureState>((state) => state.aaFeature, shallowEqual);
  // router
  const { aaFeatureId } = useParams<"aaFeatureId">();

  if (isLoading) {
    return (
      <div className={styleLayout.page}>
        <LoadingSpinner inCenter />
      </div>
    );
  }

  let genomeViewProps: IGenomeViewOwnProps;

  if (aaFeature) {
    const aaFeatureTrack: Partial<ISortableTrack> = {
      name: aaFeature.aaFeatureType.name,
      type: TRACK_TYPE.AAFEATURE,
      subTypeId: aaFeature.aaFeatureType.id,
      color: aaFeature.aaFeatureType.color,
      hidden: true,
    };
    const tracks: ISortableTrack[] = getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.PROTEIN_LOCATION]);
    tracks.push(aaFeatureTrack as ISortableTrack);

    genomeViewProps = {
      genomeId: aaFeature.genome.id,
      contigId: aaFeature.region.contig.id,
      range: getPaddedRange(aaFeature.region.range, aaFeature.region.contig.length),
      genomes: [aaFeature.genome],
      defaultTracks: tracks,
      attributesWidgets: [
        <AttributesListWidget
          attributes={renderAaFeatureAttributesWidgets(aaFeature)}
          key={"aaFeature-summary-view-widget-1"}
        />,
      ],
      title: aaFeature.name,
      subtitle: "Genome View",
      headerTags: [
        <Tag
          label={aaFeature?.aaFeatureType.name}
          key={"aaFeature-genome-view-tag-1"}
          intentColor={aaFeature?.aaFeatureType?.color}
        />,
      ],
    };
  }

  return (
    <GenericEntityGenomeView
      genomeViewProps={genomeViewProps}
      loadEntityData={() => {
        if (aaFeatureId) {
          dispatch(loadAaFeature(Number(aaFeatureId)));
        }
      }}
    />
  );
};

export default AaFeatureGenomeView;
